import { FC } from "react";
import { useMessageRedirect, useTrans } from "../../../hooks";
import { Box, Flex, HStack, Stack, VStack, Text, Tag } from "@chakra-ui/react";
import {
    AppRenderDetailChatItem,
    AppRenderDetailItem,
} from "../../../components";
import {
    Company,
    Internship,
    InternshipSchedule,
    InternshipScheduleSeat,
    Service,
    Training,
    Typology,
    User,
} from "../../../apis";
import { APPC, UTIL } from "../../../config";

export const InternshipDetailView: FC<{
    data: InternshipScheduleSeat;
    internshipCoordinator: User[];
}> = ({ data, internshipCoordinator }) => {
    const { t } = useTrans();
    const { redirectMessageO2O } = useMessageRedirect();
    const dayjs = UTIL.getDayJs();

    return (
        <VStack gap={8} alignItems={"stretch"}>
            <Stack
                gap={8}
                direction={{
                    base: "column",
                    sm: "column",
                    md: "row",
                }}
            >
                <AppRenderDetailItem
                    label={t("ent.Company:name.label")}
                    value={
                        typeof data?.internshipSchedule !== "string" &&
                        typeof data?.internshipSchedule?.internship !== "string"
                            ? (
                                  data?.internshipSchedule?.internship
                                      ?.company as Company
                              )?.name
                            : ""
                    }
                />
                <AppRenderDetailItem
                    label={t("ent.Internship:name.label")}
                    value={`${
                        typeof data?.internshipSchedule !== "string" &&
                        typeof data?.internshipSchedule?.internship !== "string"
                            ? data?.internshipSchedule?.internship?.name
                            : ""
                    } (${
                        typeof data?.internshipSchedule !== "string"
                            ? data?.internshipSchedule?.name
                            : ""
                    })`}
                />
            </Stack>
            <Stack
                gap={8}
                direction={{
                    base: "column",
                    sm: "column",
                    md: "row",
                }}
            >
                <AppRenderDetailChatItem
                    label={t("ent.Internship:internshipCoordinator.label")}
                    users={internshipCoordinator || []}
                    onClick={(user) =>
                        redirectMessageO2O(
                            user,
                            (
                                (
                                    (
                                        data?.internshipSchedule as InternshipSchedule
                                    )?.internship as Internship
                                )?.company as Company
                            )?.id
                        )
                    }
                />
                <AppRenderDetailChatItem
                    label={t("ent.Internship:internshipTutor.label")}
                    users={
                        typeof data?.internshipSchedule !== "string" &&
                        typeof data?.internshipSchedule?.internship !==
                            "string" &&
                        Array.isArray(
                            data?.internshipSchedule?.internship
                                ?.internshipTutors
                        )
                            ? (data?.internshipSchedule?.internship
                                  ?.internshipTutors as User[])
                            : []
                    }
                    onClick={(user) =>
                        redirectMessageO2O(
                            user,
                            (
                                (
                                    (
                                        data?.internshipSchedule as InternshipSchedule
                                    )?.internship as Internship
                                )?.company as Company
                            )?.id
                        )
                    }
                />
            </Stack>
            <Stack
                gap={8}
                direction={{
                    base: "column",
                    sm: "column",
                    md: "row",
                }}
            >
                <AppRenderDetailItem
                    label={t("ent.InternshipSchedule:totalTime.label")}
                    value={`${
                        (data?.internshipSchedule as InternshipSchedule)
                            ?.totalTime / 60
                    } ${t("cmn:label.hours")}`}
                />
                <AppRenderDetailItem
                    label={t("ent.InternshipScheduleSeat:noOfSeats.label")}
                    value={`${data?.noOfAssignedSeats || 0} / ${data?.noOfSeats}`}
                />
            </Stack>
            <Stack
                gap={8}
                direction={{
                    base: "column",
                    sm: "column",
                    md: "row",
                }}
            >
                <AppRenderDetailItem
                    label={t("cmn:label.date")}
                    value={`${dayjs(
                        (data?.internshipSchedule as InternshipSchedule)
                            ?.startDate
                    ).format(APPC.APP_DATE_FORMAT)} - ${dayjs(
                        (data?.internshipSchedule as InternshipSchedule)
                            ?.endDate
                    ).format(APPC.APP_DATE_FORMAT)}`}
                />
                {!(data?.internshipSchedule as InternshipSchedule)
                    ?.isScheduleDetail && (
                    <AppRenderDetailItem
                        label={t("cmn:label.time")}
                        value={`${dayjs
                            .utc(
                                (data?.internshipSchedule as InternshipSchedule)
                                    ?.startTime
                            )
                            .format(APPC.APP_TIME_FORMAT)} - ${dayjs
                            .utc(
                                (data?.internshipSchedule as InternshipSchedule)
                                    ?.endTime
                            )
                            .format(APPC.APP_TIME_FORMAT)}`}
                    />
                )}
            </Stack>
            {!(data?.internshipSchedule as InternshipSchedule)
                ?.isScheduleDetail && (
                <Stack
                    gap={8}
                    direction={{
                        base: "column",
                        sm: "column",
                        md: "row",
                    }}
                >
                    <AppRenderDetailItem
                        label={t("ent.InternshipSchedule:breakDuration.label")}
                        value={`${
                            (data?.internshipSchedule as InternshipSchedule)
                                ?.breakDuration
                        } ${t("cmn:label.min")}`}
                    />
                    <AppRenderDetailItem
                        label={t("ent.InternshipSchedule:weekDays.label")}
                    >
                        <Flex gap={2}>
                            {Object.keys(
                                (data?.internshipSchedule as InternshipSchedule)
                                    ?.weekDays || {}
                            ).map((day) => (
                                <Tag
                                    key={day}
                                    w={"50px"}
                                    justifyContent={"center"}
                                    bg={
                                        (
                                            data?.internshipSchedule as InternshipSchedule
                                        )?.weekDays[day]
                                            ? "var(--chakra-colors-primary)"
                                            : "var(--chakra-colors-greyT95)"
                                    }
                                    color={
                                        (
                                            data?.internshipSchedule as InternshipSchedule
                                        )?.weekDays[day]
                                            ? "var(--chakra-colors-white)"
                                            : "var(--chakra-colors-contentT70)"
                                    }
                                >
                                    {t(`cmn:label.${day}`)}
                                </Tag>
                            ))}
                        </Flex>
                    </AppRenderDetailItem>
                </Stack>
            )}
            <Stack
                gap={8}
                direction={{
                    base: "column",
                    sm: "column",
                    md: "row",
                }}
            >
                <AppRenderDetailItem
                    label={t("ent.Internship:training.label")}
                    value={
                        (
                            (
                                (data?.internshipSchedule as InternshipSchedule)
                                    ?.internship as Internship
                            )?.training as Training
                        )?.name
                    }
                />
                <AppRenderDetailItem
                    label={t("ent.InternshipSchedule:trainingYear.label")}
                >
                    <Flex gap={3}>
                        {typeof data?.internshipSchedule !== "string" &&
                            typeof data?.internshipSchedule?.trainingYear !==
                                "string" && (
                                <Tag
                                    size={"sm"}
                                    bg={"var(--chakra-colors-primaryT80)"}
                                >
                                    <Box
                                        display="inline-block"
                                        w="12px"
                                        h="12px"
                                        mr="2"
                                        bg={
                                            data?.internshipSchedule
                                                ?.trainingYear.color
                                        }
                                        borderRadius={"full"}
                                    ></Box>
                                    {`${data?.internshipSchedule?.trainingYear.code} - ${data?.internshipSchedule?.trainingYear.year}`}
                                </Tag>
                            )}
                    </Flex>
                </AppRenderDetailItem>
            </Stack>
            {typeof data?.internshipSchedule !== "string" &&
                typeof data?.internshipSchedule?.internship !== "string" &&
                ((data?.internshipSchedule?.internship?.service as Service)
                    ?.name ||
                    (data?.internshipSchedule?.internship?.typology as Typology)
                        ?.name) && (
                    <Stack
                        gap={8}
                        direction={{
                            base: "column",
                            sm: "column",
                            md: "row",
                        }}
                    >
                        {(
                            data?.internshipSchedule?.internship
                                ?.service as Service
                        )?.name && (
                            <AppRenderDetailItem
                                label={t("ent.Internship:service.label")}
                            >
                                <>
                                    <Text textStyle={"mediumSm"} mb={2}>
                                        {
                                            (
                                                data?.internshipSchedule
                                                    ?.internship
                                                    ?.service as Service
                                            )?.name
                                        }
                                    </Text>
                                    <Box ml={3}>
                                        <AppRenderDetailChatItem
                                            users={
                                                typeof data?.internshipSchedule !==
                                                    "string" &&
                                                typeof data?.internshipSchedule
                                                    ?.internship !== "string" &&
                                                typeof data?.internshipSchedule
                                                    ?.internship?.service !==
                                                    "string" &&
                                                Array.isArray(
                                                    data?.internshipSchedule
                                                        ?.internship?.service
                                                        ?.users
                                                )
                                                    ? (data?.internshipSchedule
                                                          ?.internship?.service
                                                          ?.users as User[])
                                                    : []
                                            }
                                            onClick={(user) =>
                                                redirectMessageO2O(
                                                    user,
                                                    (
                                                        (
                                                            (
                                                                data?.internshipSchedule as InternshipSchedule
                                                            )
                                                                ?.internship as Internship
                                                        )?.company as Company
                                                    )?.id
                                                )
                                            }
                                        />
                                    </Box>
                                </>
                            </AppRenderDetailItem>
                        )}
                        {(
                            data?.internshipSchedule?.internship
                                ?.typology as Typology
                        )?.name && (
                            <AppRenderDetailItem
                                label={t("ent.Internship:typology.label")}
                                value={
                                    (
                                        data?.internshipSchedule?.internship
                                            ?.typology as Typology
                                    )?.name
                                }
                            />
                        )}
                    </Stack>
                )}
            {(data?.internshipSchedule as InternshipSchedule)
                ?.isScheduleDetail && (
                <AppRenderDetailItem
                    label={t("ent.InternshipSchedule:isScheduleDetail.label")}
                >
                    <Flex gap={3} flexWrap={"wrap"}>
                        {(
                            (data?.internshipSchedule as InternshipSchedule)
                                ?.timeSlots || []
                        ).map((week, weekIndex) => (
                            <VStack
                                key={weekIndex}
                                bg={"var(--chakra-colors-greyT95)"}
                                borderRadius={8}
                                p={3}
                                flexShrink={0}
                                alignItems={"flex-start"}
                            >
                                <Text
                                    textStyle={"mediumMd"}
                                    textDecoration={"underline"}
                                    mb={2}
                                >
                                    {`${weekIndex + 1} week`}
                                </Text>
                                {week?.length < 7 &&
                                    weekIndex === 0 &&
                                    week[0].day !== "mon" &&
                                    [...Array(7 - week.length)].map(
                                        (_, index) => (
                                            <Box
                                                key={`weekStart-${index}`}
                                                h="20px"
                                            />
                                        )
                                    )}
                                {week.map((day, dayIndex) => {
                                    const dayKey = `${dayjs(day.date).format(
                                        APPC.APP_HTML_DATE_FORMAT
                                    )}`;
                                    const renderDayRow = () => (
                                        <HStack
                                            key={dayKey}
                                            w={"full"}
                                            h="20px"
                                            gap={2}
                                        >
                                            <Text
                                                w={"80px"}
                                                textStyle={"mediumXs"}
                                                color={
                                                    !day.isChecked
                                                        ? "var(--chakra-colors-contentT70)"
                                                        : ""
                                                }
                                            >
                                                {dayjs(day.date).format(
                                                    "ddd DD MMM"
                                                )}
                                            </Text>
                                            <Text
                                                w={"95px"}
                                                textStyle={"mediumXs"}
                                                color={
                                                    !day.isChecked
                                                        ? "var(--chakra-colors-contentT70)"
                                                        : ""
                                                }
                                            >
                                                {day.isChecked
                                                    ? `${dayjs
                                                          .utc(day.startTime)
                                                          .format(
                                                              APPC.APP_TIME_FORMAT
                                                          )} - ${dayjs
                                                          .utc(day.endTime)
                                                          .format(
                                                              APPC.APP_TIME_FORMAT
                                                          )}`
                                                    : "-"}
                                            </Text>
                                            <Text
                                                w={"55px"}
                                                textStyle={"mediumXs"}
                                                color={
                                                    !day.isChecked
                                                        ? "var(--chakra-colors-contentT70)"
                                                        : ""
                                                }
                                            >
                                                {day.isChecked
                                                    ? `${day?.breakDuration} ${t("cmn:label.min")}`
                                                    : "-"}
                                            </Text>
                                        </HStack>
                                    );
                                    if (!dayIndex) {
                                        return (
                                            <VStack key={dayKey}>
                                                <HStack
                                                    w={"full"}
                                                    h="20px"
                                                    gap={2}
                                                >
                                                    <Text
                                                        w={"80px"}
                                                        textStyle={"semiBoldXs"}
                                                    >
                                                        {t("cmn:label.date")}
                                                    </Text>
                                                    <Text
                                                        w={"95px"}
                                                        textStyle={"semiBoldXs"}
                                                    >
                                                        {t("cmn:label.time")}
                                                    </Text>
                                                    <Text
                                                        w={"55px"}
                                                        textStyle={"semiBoldXs"}
                                                    >
                                                        {t(
                                                            "ent.InternshipSchedule:breakDuration.label"
                                                        )}
                                                    </Text>
                                                </HStack>
                                                {renderDayRow()}
                                            </VStack>
                                        );
                                    }
                                    return renderDayRow();
                                })}
                                {(
                                    data?.internshipSchedule as InternshipSchedule
                                )?.timeSlots &&
                                    week?.length < 7 &&
                                    weekIndex + 1 ===
                                        (
                                            data?.internshipSchedule as InternshipSchedule
                                        )?.timeSlots.length &&
                                    week[0].day === "mon" &&
                                    [...Array(7 - week.length)].map(
                                        (_, index) => (
                                            <Box
                                                key={`weekEnd-${index}`}
                                                h="20px"
                                            />
                                        )
                                    )}
                            </VStack>
                        ))}
                    </Flex>
                </AppRenderDetailItem>
            )}
        </VStack>
    );
};
